import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { didCancel, task } from 'ember-concurrency';
import type { PropertyAccessChange } from 'lumen/components/settings/users/modals/modal-property-access';
import type Userhotelrelationship from 'lumen/models/userhotelrelationship';
import type { PropertyAccessModel } from 'lumen/routes/hotel/settings/users/property-access';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { timeout } from 'lumen/utils/waiters';

interface SortState {
  isAscending: boolean;
  valuePath: string;
}

const SORT_KEY_MAP: Record<string, string> = {
  createdAt: 'date',
  propertyName: 'name',
};

export interface BulkDeletePayload {
  exclude: boolean;
  q?: string;
  subscription_ids: number[];
  user_id: string;
}

export default class HotelSettingsUsersPropertyAccessController extends Controller {
  @tracked declare model: PropertyAccessModel;

  // `as` aliases avoid a query-param collision with the active parent `users`
  // controller; cast because Ember's types omit the `as` option.
  queryParams = [
    'userId',
    'userEmail',
    { role: { as: 'access-role' } },
    { product: { as: 'access-product' } },
  ] as unknown as Controller['queryParams'];

  @service('ajax') declare ajaxService: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked currentPage = 0;
  @tracked product: string | null = null;
  @tracked relationships: Userhotelrelationship[] = [];
  @tracked role: string | null = null;
  @tracked searchQuery: string | null = null;
  @tracked searchText = '';
  @tracked sortBy: string | null = null;
  @tracked sortDirection: 'asc' | 'desc' = 'asc';
  @tracked totalResults = 0;
  userEmail = '';
  userId = '';

  debouncedSearchTask = task({ restartable: true }, async (value: string) => {
    await timeout();

    const trimmed = value.trim();

    this.searchQuery = trimmed || null;
    this.currentPage = 0;
    this.relationships = [];

    await this.fetchTask.perform();
  });

  fetchTask = task({ keepLatest: true }, async () => {
    const params: Record<string, string | number> = {
      user_id: this.userId,
      page: this.currentPage,
    };

    if (this.searchQuery) {
      params['q'] = this.searchQuery;
    }

    if (this.sortBy) {
      params['sort_by'] = SORT_KEY_MAP[this.sortBy] ?? this.sortBy;
      params['sort_order'] = this.sortDirection;
    }

    if (this.role) {
      params['role'] = this.role;
    }

    if (this.product) {
      params['product'] = this.product;
    }

    if (this.currentPage === 0) {
      this.store.unloadAll('userhotelrelationship');
    }

    const results = await this.store.query('userhotelrelationship', params);

    const meta = results.meta as { results_count: number };
    this.totalResults = meta.results_count;

    const newRecords = results.slice() as unknown as Userhotelrelationship[];

    if (this.currentPage === 0) {
      this.relationships = newRecords;
    } else {
      this.relationships = [...this.relationships, ...newRecords];
    }
  });

  get hasMore(): boolean {
    return this.relationships.length < this.totalResults;
  }

  get isLoading(): boolean {
    return this.currentPage === 0 && this.fetchTask.isRunning;
  }

  get isLoadingMore(): boolean {
    return this.currentPage > 0 && this.fetchTask.isRunning;
  }

  get isOwnUser(): boolean {
    return this.userId === this.hotelAndMarketService.user?.id;
  }

  get sorts(): SortState[] {
    if (!this.sortBy) {
      return [];
    }

    return [
      {
        valuePath: this.sortBy,
        isAscending: this.sortDirection === 'asc',
      },
    ];
  }

  initializeFromModel(): void {
    this.searchText = '';
    this.searchQuery = null;
    this.sortBy = null;
    this.sortDirection = 'asc';
    this.currentPage = 0;
    this.totalResults = this.model.totalResults;
    this.relationships = this.model.relationships;
  }

  private async reloadRelationships(): Promise<void> {
    this.currentPage = 0;
    this.relationships = [];

    try {
      await this.fetchTask.perform();
    } catch (error) {
      // Ignore ember concurrency task cancellation errors because the fetch task gets canceled when a new fetch starts or the route is torn down.
      if (!didCancel(error)) {
        // But re-throw the non-cancelation error
        throw error;
      }
    }
  }

  @action
  async addPropertyAccess(subscriptionIds: number[]): Promise<void> {
    try {
      await this.ajaxService.request('userhotelrelationships/', {
        method: 'POST',
        data: {
          subscription_ids: subscriptionIds,
          user_id: this.userId,
        },
      });

      this.toast.success(this.intl.t('settings.users.add_property.success'), {
        showIcon: true,
      });

      await this.reloadRelationships();
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.add_property.failure'), {
        showIcon: true,
      });
    }
  }

  @action
  async bulkDelete(payload: BulkDeletePayload): Promise<boolean> {
    try {
      await this.ajaxService.request('userhotelrelationships/', {
        method: 'DELETE',
        data: payload,
      });

      this.toast.success(this.intl.t('settings.users.bulk.delete_success'), {
        showIcon: true,
      });

      this.router.refresh('hotel.settings.users.property-access');
      return true;
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.bulk.delete_failure'), {
        showIcon: true,
      });

      this.router.refresh('hotel.settings.users.property-access');
      return false;
    }
  }

  @action
  close(): void {
    this.router.transitionTo('hotel.settings.users');
  }

  @action
  async deleteRelationship(
    subscriptionId: number,
    propertyName: string,
  ): Promise<void> {
    const confirmed = await this.alerting.showConfirmWarningAlert({
      text: this.intl.t('settings.users.property_access.confirm_delete', {
        userName: this.userEmail,
        propertyName,
      }),
      title: this.intl.t('settings.users.property_access.remove_property'),
    });

    if (!confirmed) {
      return;
    }

    try {
      await this.ajaxService.request('userhotelrelationships/', {
        method: 'DELETE',
        data: {
          subscription_ids: [subscriptionId],
          user_id: this.userId,
        },
      });

      const record = (
        this.store.peekAll(
          'userhotelrelationship',
        ) as unknown as Userhotelrelationship[]
      ).find((relationship) => relationship.subscriptionId === subscriptionId);

      if (record) {
        this.store.unloadRecord(record);
      }

      this.relationships = this.relationships.filter(
        (relationship) => relationship.subscriptionId !== subscriptionId,
      );
      this.totalResults = Math.max(0, this.totalResults - 1);

      this.toast.success(
        this.intl.t('settings.users.property_access.delete_success'),
        { showIcon: true },
      );
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('settings.users.property_access.delete_failure'),
        { showIcon: true },
      );
      await this.reloadRelationships();
    }
  }

  @action
  onLoadMore(): void {
    if (!this.hasMore || this.fetchTask.isRunning) {
      return;
    }

    this.currentPage = this.currentPage + 1;
    void this.fetchTask.perform();
  }

  @action
  onProductChange(product: string | null): void {
    this.product = product;
    void this.reloadRelationships();
  }

  @action
  onRoleChange(role: string | null): void {
    this.role = role;
    void this.reloadRelationships();
  }

  @action
  onSearchChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
    void this.debouncedSearchTask.perform(this.searchText);
  }

  @action
  onSortChange(sorts: SortState[]): void {
    if (sorts[0]) {
      this.sortBy = sorts[0].valuePath;
      this.sortDirection = sorts[0].isAscending ? 'asc' : 'desc';
    }

    this.currentPage = 0;
    this.relationships = [];
    void this.fetchTask.perform();
  }

  @action
  async saveChanges(changes: PropertyAccessChange[]): Promise<boolean> {
    if (this.isOwnUser) {
      const confirmed = await this.alerting.showConfirmWarningAlert({
        text: this.intl.t('settings.hotel_access.confirm_edit_self'),
      });

      if (!confirmed) {
        return false;
      }
    }

    try {
      for (const change of changes) {
        await this.ajaxService.request('userhotelrelationships/', {
          method: 'PUT',
          data: {
            subscription_ids: change.subscriptionIds,
            user_id: change.userId,
            ...(change.accessRight && { access_right: change.accessRight }),
            ...(change.accessibleProducts && {
              accessible_products: change.accessibleProducts,
            }),
          },
        });
      }

      this.toast.success(this.intl.t('settings.users.bulk_edit.success'), {
        showIcon: true,
      });

      await this.reloadRelationships();
      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.bulk_edit.failure'), {
        showIcon: true,
      });
      await this.reloadRelationships();
      return false;
    }
  }
}
